import { useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useMutation, useQuery } from '@tanstack/react-query'
import { KeyRound, AlertCircle } from 'lucide-react'
import toast from 'react-hot-toast'
import { authApi } from '../api/auth'
import { PageSpinner } from '../components/ui/Spinner'

interface FormData { newPassword: string; confirmPassword: string }

export function ResetPasswordPage() {
  useEffect(() => { document.title = 'Reset Password' }, [])
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''

  // Pre-validate the token on page load so we can show "expired" instead of
  // letting the user fill in the form just to be told it didn't work.
  const validation = useQuery({
    queryKey: ['reset-token-validity', token],
    queryFn: () => authApi.validateResetToken(token).then(r => r.data.valid),
    enabled: token.length >= 32,
    retry: false,
    staleTime: Infinity,
  })

  const { register, handleSubmit, watch, formState: { errors } } = useForm<FormData>()

  const mutation = useMutation({
    mutationFn: (dto: FormData) => authApi.resetPassword(token, dto.newPassword),
    onSuccess: () => {
      toast.success('Password reset. Sign in with your new password.')
      navigate('/login', { replace: true })
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message ?? 'Reset link is invalid or has expired.'
      toast.error(typeof msg === 'string' ? msg : 'Reset failed.')
    },
  })

  const newPassword = watch('newPassword')

  // Token missing entirely — render the invalid panel directly.
  const tokenMissing = !token || token.length < 32
  // Validation completed and explicitly returned false, OR the validation request failed.
  const tokenInvalid = validation.isError || validation.data === false

  if (tokenMissing || tokenInvalid) {
    return <InvalidLinkPanel />
  }

  if (validation.isLoading) {
    return <PageSpinner />
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary-600 mb-4">
            <KeyRound className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-2xl font-semibold text-gray-900">Choose a new password</h1>
          <p className="text-sm text-gray-500 mt-1">At least 6 characters.</p>
        </div>

        <form
          onSubmit={handleSubmit(d => mutation.mutate(d))}
          className="card p-6 space-y-4"
        >
          <div>
            <label className="label">New password</label>
            <input
              {...register('newPassword', {
                required: 'New password is required',
                minLength: { value: 6, message: 'At least 6 characters' },
              })}
              type="password"
              autoComplete="new-password"
              className="input"
              placeholder="••••••••"
            />
            {errors.newPassword && (
              <p className="text-xs text-red-600 mt-1">{errors.newPassword.message}</p>
            )}
          </div>
          <div>
            <label className="label">Confirm password</label>
            <input
              {...register('confirmPassword', {
                required: 'Please confirm your password',
                validate: v => v === newPassword || 'Passwords do not match',
              })}
              type="password"
              autoComplete="new-password"
              className="input"
              placeholder="••••••••"
            />
            {errors.confirmPassword && (
              <p className="text-xs text-red-600 mt-1">{errors.confirmPassword.message}</p>
            )}
          </div>
          <button
            type="submit"
            disabled={mutation.isPending}
            className="btn-primary btn w-full"
          >
            {mutation.isPending ? 'Resetting...' : 'Reset password'}
          </button>
          <div className="text-center text-sm">
            <Link to="/login" className="text-primary-600 hover:underline">
              Back to sign in
            </Link>
          </div>
        </form>
      </div>
    </div>
  )
}

function InvalidLinkPanel() {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-amber-500 mb-4">
            <AlertCircle className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-2xl font-semibold text-gray-900">Link expired or invalid</h1>
          <p className="text-sm text-gray-500 mt-1">
            Reset links are single-use and expire 30 minutes after they're sent.
          </p>
        </div>

        <div className="card p-6 space-y-3 text-center">
          <p className="text-sm text-gray-700">
            Request a new link to continue.
          </p>
          <Link to="/forgot-password" className="btn-primary btn w-full">
            Request a new reset link
          </Link>
          <Link to="/login" className="block text-sm text-primary-600 hover:underline">
            Back to sign in
          </Link>
        </div>
      </div>
    </div>
  )
}
